import { warn } from 'helper/debug';

/*
 * Helper functions
 */

function _getObjectByString(o, s): Object | string | undefined {
	s = s.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
	s = s.replace(/^\./, ''); // strip a leading dot
	const a = s.split('.');
	for (let i = 0, n = a.length; i < n; ++i) {
		const k = a[i];
		if (k in o) {
			o = o[k];
		} else {
			return;
		}
	}
	return o;
}

function _getDataAttribute(attributeName: string): string {
	if (process.env.NODE_ENV === 'test') {
		return '';
	}

	let attribute: string | null = '';
	if (document.body && document.body.getAttribute) {
		attribute = document.body.getAttribute(`data-${attributeName}`);
	}

	if (!attribute || attribute === '') {
		warn(`Missing data attribute on <body>: ${attributeName}`);
		return '';
	}

	return attribute;
}

function _getGlobalVar(globalVarName: string, preventWarning: boolean = false): string {
	if (process.env.NODE_ENV === 'test') {
		return '';
	}

	const globalVar = _getObjectByString(window, globalVarName);
	if (!globalVar && !preventWarning) {
		warn(`Missing global on window: ${globalVarName}`);
		return '';
	}

	if (typeof globalVar !== 'string') {
		return '';
	}

	return globalVar;
}

function _getGlobalVarAsBoolean(globalVarName: string): boolean {
	return !!_getObjectByString(window, globalVarName);
}

function _getGlobalVarAsString(globalVarName: string): string {
	return _getGlobalVar(globalVarName).toString();
}

function _getGlobalVarAsNumber(globalVarName: string): number {
	const value = _getObjectByString(window, globalVarName) as string;

	return typeof value === 'number' ? value : parseInt(value, 10);
}

function _getGlobalObject(globalVarName): any {
	if (process.env.NODE_ENV === 'test') {
		return {};
	}

	const globalVar = _getObjectByString(window, globalVarName);
	if (!globalVar) {
		warn(`Missing global on window: ${globalVarName}`);
		return {};
	}

	if (typeof globalVar !== 'object') {
		return {};
	}

	return globalVar;
}

function _getViewConfig(view: string): Object {
	try {
		const config = (window as any).ReactApp.Views[view];
		return config || {};
	} catch (err) {
		return {};
	}
}

/*
 * Mapping functions
 */
function getBaseName(): string {
	return _getDataAttribute('basename');
}

function getUrlSpringAPI(): string {
	if (process.env.REACT_APP_MOCK_API === 'true') {
		return 'https://localhost:3002/spring/api/v1';
	}

	return _getGlobalVar('ReactApp.Env.urlSpringAPI');
}

function getUrlMagnoliaAPI(): string {
	if (process.env.REACT_APP_MOCK_API === 'true') {
		return 'https://localhost:3003/magnolia/api/v1';
	}

	return _getGlobalVar('ReactApp.Env.urlMagnoliaAPI');
}

function getUrlMagnoliaAPICoach(): string {
	return _getGlobalVar('ReactApp.Env.urlMagnoliaAPICoach');
}

export type LinkSchema = {
	href: string;
	openInNewWindow: boolean;
};

export type AppConfigType = {
	platform: string;
	platformOS: string;
	env: {
		id: string;
		appLaunched: boolean;
		remlogHost?: string;
		i18nDebuggingEnabled: boolean;
		devToolsEnabled: boolean;
		heartbeatInterval: number;
		cachingTimestamp: string;
		urlSpringAPI: string;
		urlMagnoliaAPI: string;
		urlMagnoliaAPICoach: string;
		language: string;
		oidcClientId: string;
		baseName: string;
		coachBaseUrl: string;
		baseUrlUi: string;
		migrosImpulsURL: string;
		migrosImpulsCoachLandingPage: LinkSchema;
		migrosImpulsNewsletterPage: LinkSchema;
		oneTrustEnabled: boolean;
		msrc: {
			env: string;
			webApiKey: string;
			version: string;
		};
		msrcConfig: {
			account: string;
			authority: string;
			language: string;
			loginEndpoint: string;
			logoutEndpoint: string;
			accessTokenEndpoint: string;
			linkCategories: { key: string; label: string }[];
			links: { label: string; link: string; category: string }[];
		};
	};
	views: {
		Profile: {
			links: { title: string; href: string; openInNewWindow: boolean }[];
		};
		Error40x: {
			subtitle: string;
			text: string;
			title: string;
		};
		Dashboard: {
			links: { title: string; href: string; openInNewWindow: boolean }[];
		};
		Favorites: {
			disableRetailerContext: boolean;
		};
	};
};

const appConfig: AppConfigType = {
	platform: `${_getGlobalVarAsString('ReactApp.Platform')}`,
	platformOS: `${_getGlobalVarAsString('ReactApp.PlatformOS')}`,
	env: {
		id: `${_getGlobalVarAsString('ReactApp.Env.id')}`,
		appLaunched: _getGlobalVarAsBoolean('ReactApp.Env.appLaunched'),
		remlogHost: `${_getGlobalVarAsString('ReactApp.Env.remlogHost')}`,
		coachBaseUrl: `${_getGlobalVarAsString('ReactApp.Env.coachBaseUrl')}`,
		i18nDebuggingEnabled: _getGlobalVarAsBoolean('ReactApp.Env.coachI18nDebug'),
		devToolsEnabled: _getGlobalVarAsBoolean('ReactApp.Env.enableDevTools'),
		heartbeatInterval: _getGlobalVarAsNumber('ReactApp.Env.heartbeatInterval'),
		cachingTimestamp: `${_getGlobalVarAsString('ReactApp.Env.cachingTimestamp')}`,
		urlSpringAPI: getUrlSpringAPI(),
		urlMagnoliaAPI: getUrlMagnoliaAPI(),
		urlMagnoliaAPICoach: getUrlMagnoliaAPICoach(),
		language: _getGlobalVarAsString('ReactApp.Env.language'),
		oidcClientId: _getGlobalVarAsString('ReactApp.Env.oidcClientId'),
		oneTrustEnabled: _getGlobalVarAsBoolean('ReactApp.Env.oneTrustEnabled'),
		baseName: getBaseName(),
		baseUrlUi: _getGlobalVarAsString('ReactApp.Env.baseUrlUi') || '',
		migrosImpulsURL: `${_getGlobalVarAsString('ReactApp.Env.migrosImpulsURL')}`,
		migrosImpulsCoachLandingPage: _getGlobalObject('ReactApp.Env.migrosImpulsCoachLandingPage') as any,
		migrosImpulsNewsletterPage: _getGlobalObject('ReactApp.Env.migrosImpulsNewsletterPage') as any,
		msrc: _getGlobalObject('ReactApp.Env.msrc'),
		msrcConfig: _getGlobalObject('ReactApp.Env.msrcConfig'),
	},
	views: {
		Profile: _getViewConfig('Profile') as any,
		Error40x: _getViewConfig('Error40x') as any,
		Dashboard: _getViewConfig('Dashboard') as any,
		Favorites: _getViewConfig('Favorites') as any,
	},
};

export default appConfig;
